/**
 * Colour scheme preference.
 *
 * "system" follows the device setting, which is what most people want on a
 * phone where the OS flips to dark at sunset. The two explicit values pin the
 * dashboard regardless of the device, and are remembered per browser.
 *
 * The pure helpers here are separated from the DOM so they can be unit tested.
 */
export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'iot-dashboard-theme';

/** Keep in step with --panel in global.css: it tints the mobile browser chrome. */
export const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: '#ffffff',
  dark: '#1a1a19',
};

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

/** What the page should actually render as, given the preference and the device. */
export function resolveTheme(preference: ThemePreference, systemPrefersDark: boolean): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
}

function media(): MediaQueryList | undefined {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : undefined;
}

export function systemPrefersDark(): boolean {
  return media()?.matches ?? false;
}

export function readStoredPreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(raw) ? raw : 'system';
  } catch {
    // Private mode or blocked storage: fall back to the device setting.
    return 'system';
  }
}

function storePreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Not fatal: the choice simply will not survive a reload.
  }
}

/**
 * Writes the preference to <html>. A "system" preference removes the attribute
 * so the prefers-color-scheme media query in the stylesheet takes over.
 */
export function applyTheme(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference, systemPrefersDark());
  const root = document.documentElement;
  if (preference === 'system') delete root.dataset.theme;
  else root.dataset.theme = preference;
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = THEME_COLORS[resolved];
  return resolved;
}

export function setThemePreference(preference: ThemePreference): ResolvedTheme {
  storePreference(preference);
  return applyTheme(preference);
}

/** Calls back when the device flips between light and dark. */
export function watchSystemTheme(onChange: () => void): () => void {
  const mq = media();
  if (!mq) return () => undefined;
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}
